import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PerformanceRow } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

// The campaign table on the Watchdog page: Google Ads campaigns, their ad groups and ads over a
// date range, with the columns a buyer picks. Rows arrive as per-ad sums (GET
// /google-watchdog/performance); every rate is worked out here from summed totals, so a campaign's
// rate is its ads' totals divided, never an average of its ads' rates. Pure functions, no React.

// ── Columns ──────────────────────────────────────────────────────────────────

export interface PerfTotals {
  spend: number;
  impressions: number;
  clicks: number;
  sessions: number;
  atc: number;
  orders: number;
  revenue: number;
  ncOrders: number;
  ncRevenue: number;
  googleValue: number;
  googleConversions: number;
}

export type PerfFormat = 'money' | 'money2' | 'ratio' | 'pct' | 'count';
/** Where a column's numbers come from: Google Ads' own reporting, or Triple Whale's attribution. */
export type PerfSource = 'google' | 'triple_whale';

export interface PerfColumn {
  key: string;
  label: string;
  description: string;
  source: PerfSource;
  format: PerfFormat;
  value: (t: PerfTotals) => number | null;
}

const per = (n: number, d: number): number | null => (d > 0 ? n / d : null);

export const PERF_COLUMNS: PerfColumn[] = [
  {
    key: 'spend',
    label: 'Spend',
    description: 'Ad spend',
    source: 'google',
    format: 'money2',
    value: t => t.spend,
  },
  {
    key: 'roas',
    label: 'ROAS',
    description:
      'Return on ad spend (ROAS): Triple Whale revenue, excluding sales tax, per $1 spent',
    source: 'triple_whale',
    format: 'ratio',
    value: t => per(t.revenue, t.spend),
  },
  {
    key: 'costPerSession',
    label: 'Cost / session',
    description: 'Spend per site visit',
    source: 'triple_whale',
    format: 'money2',
    value: t => per(t.spend, t.sessions),
  },
  {
    key: 'revenuePerSession',
    label: 'Rev / session',
    description: 'Revenue, excluding sales tax, per site visit',
    source: 'triple_whale',
    format: 'money2',
    value: t => per(t.revenue, t.sessions),
  },
  {
    key: 'atcRate',
    label: 'Add to cart',
    description: 'Share of visits that added to cart',
    source: 'triple_whale',
    format: 'pct',
    value: t => per(t.atc, t.sessions),
  },
  {
    key: 'convRate',
    label: 'Conv. rate',
    description: 'Share of visits that ordered',
    source: 'triple_whale',
    format: 'pct',
    value: t => per(t.orders, t.sessions),
  },
  {
    key: 'sessionsPerClick',
    label: 'Sessions / click',
    description: 'Site visits per ad click',
    source: 'triple_whale',
    format: 'pct',
    value: t => per(t.sessions, t.clicks),
  },
  {
    key: 'aov',
    label: 'AOV',
    description: 'Average order value (AOV), excluding sales tax',
    source: 'triple_whale',
    format: 'money',
    value: t => per(t.revenue, t.orders),
  },
  {
    key: 'cpa',
    label: 'CPA',
    description: 'Cost per acquisition (CPA): spend per Triple Whale order',
    source: 'triple_whale',
    format: 'money2',
    value: t => per(t.spend, t.orders),
  },
  {
    key: 'cpc',
    label: 'CPC',
    description: 'Cost per click (CPC)',
    source: 'google',
    format: 'money2',
    value: t => per(t.spend, t.clicks),
  },
  {
    key: 'ctr',
    label: 'CTR',
    description: 'Click-through rate (CTR): clicks per impression',
    source: 'google',
    format: 'pct',
    value: t => per(t.clicks, t.impressions),
  },
  {
    key: 'cpm',
    label: 'CPM',
    description: 'Cost per 1,000 impressions (CPM)',
    source: 'google',
    format: 'money2',
    value: t => (t.impressions > 0 ? (t.spend / t.impressions) * 1000 : null),
  },
  // Off by default; added from the column picker.
  {
    key: 'impressions',
    label: 'Impressions',
    description: 'Times an ad was shown',
    source: 'google',
    format: 'count',
    value: t => t.impressions,
  },
  {
    key: 'clicks',
    label: 'Clicks',
    description: 'Ad clicks',
    source: 'google',
    format: 'count',
    value: t => t.clicks,
  },
  {
    key: 'googleValue',
    label: 'Google conv. value',
    description: 'Conversion value as Google Ads reports it',
    source: 'google',
    format: 'money',
    value: t => t.googleValue,
  },
  {
    key: 'googleConversions',
    label: 'Google conversions',
    description: 'Conversions as Google Ads reports them',
    source: 'google',
    format: 'count',
    value: t => t.googleConversions,
  },
  {
    key: 'googleRoas',
    label: 'Google ROAS',
    description: "Google Ads' reported conversion value per $1 spent",
    source: 'google',
    format: 'ratio',
    value: t => per(t.googleValue, t.spend),
  },
  {
    key: 'googleCpa',
    label: 'Google CPA',
    description: 'Spend per conversion as Google Ads reports it',
    source: 'google',
    format: 'money2',
    value: t => per(t.spend, t.googleConversions),
  },
  {
    key: 'revenue',
    label: 'Revenue',
    description: 'Triple Whale revenue, excluding sales tax',
    source: 'triple_whale',
    format: 'money',
    value: t => t.revenue,
  },
  {
    key: 'sessions',
    label: 'Sessions',
    description: 'Site visits',
    source: 'triple_whale',
    format: 'count',
    value: t => t.sessions,
  },
  {
    key: 'atc',
    label: 'Add to carts',
    description: 'Visits that added to cart',
    source: 'triple_whale',
    format: 'count',
    value: t => t.atc,
  },
  {
    key: 'orders',
    label: 'Orders',
    description: 'Triple Whale attributed orders',
    source: 'triple_whale',
    format: 'count',
    value: t => t.orders,
  },
  {
    key: 'ncOrders',
    label: 'NC orders',
    description: 'Orders from new customers',
    source: 'triple_whale',
    format: 'count',
    value: t => t.ncOrders,
  },
  {
    key: 'ncRoas',
    label: 'NC ROAS',
    description: 'New-customer revenue (including sales tax) per $1 spent',
    source: 'triple_whale',
    format: 'ratio',
    value: t => per(t.ncRevenue, t.spend),
  },
  {
    key: 'ncCpa',
    label: 'NC CPA',
    description: 'Spend per new-customer order',
    source: 'triple_whale',
    format: 'money2',
    value: t => per(t.spend, t.ncOrders),
  },
];

export const COLUMN_BY_KEY = new Map(
  PERF_COLUMNS.map(column => [column.key, column]),
);

/** The columns the table opens with, in order, until the viewer picks their own. */
export const DEFAULT_COLUMNS = [
  'spend',
  'roas',
  'costPerSession',
  'revenuePerSession',
  'atcRate',
  'convRate',
  'sessionsPerClick',
  'aov',
  'cpa',
  'cpc',
  'ctr',
  'cpm',
];

export const SOURCE_LABEL: Record<PerfSource, string> = {
  google: 'Google Ads',
  triple_whale: 'Triple Whale',
};

export function formatPerf(
  format: PerfFormat,
  value: number | null,
  prefix: string,
): string {
  if (value == null || !Number.isFinite(value)) return '-';
  switch (format) {
    case 'money':
      return `${value < 0 ? '-' : ''}${prefix}${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    case 'money2':
      return `${value < 0 ? '-' : ''}${prefix}${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'ratio':
      return `${value.toFixed(2)}x`;
    case 'pct':
      return `${(value * 100).toFixed(1)}%`;
    case 'count':
      return Math.round(value).toLocaleString('en-US');
  }
}

// Remembered per viewer in this browser. A convenience only: anything unreadable falls back to
// the default set rather than breaking the table.
export const COLUMN_PREFS_KEY = 'sbly-tools:watchdog-performance-columns';

export function parseColumnPrefs(raw: string | null): string[] | null {
  if (!raw) return null;
  try {
    const keys = JSON.parse(raw);
    if (!Array.isArray(keys)) return null;
    const known = keys.filter(
      (key): key is string => typeof key === 'string' && COLUMN_BY_KEY.has(key),
    );
    return known.length ? Array.from(new Set(known)) : null;
  } catch {
    return null;
  }
}

// ── Rows ─────────────────────────────────────────────────────────────────────

export type PerfLevel = 'campaign' | 'adset' | 'ad';

export interface PerfNode {
  id: string;
  level: PerfLevel;
  name: string;
  store: string;
  campaignId: string;
  totals: PerfTotals;
  children: PerfNode[];
}

const zero = (): PerfTotals => ({
  spend: 0,
  impressions: 0,
  clicks: 0,
  sessions: 0,
  atc: 0,
  orders: 0,
  revenue: 0,
  ncOrders: 0,
  ncRevenue: 0,
  googleValue: 0,
  googleConversions: 0,
});

function addRow(t: PerfTotals, row: PerformanceRow): void {
  t.spend += row.spend ?? 0;
  t.impressions += row.impressions ?? 0;
  t.clicks += row.clicks ?? 0;
  t.sessions += row.sessions ?? 0;
  t.atc += row.atc ?? 0;
  t.orders += row.orders ?? 0;
  t.revenue += row.revenue ?? 0;
  t.ncOrders += row.nc_orders ?? 0;
  t.ncRevenue += row.nc_revenue ?? 0;
  t.googleValue += row.google_value ?? 0;
  t.googleConversions += row.google_conversions ?? 0;
}

export function sumTotals(nodes: PerfNode[]): PerfTotals {
  const t = zero();
  for (const node of nodes) {
    for (const key of Object.keys(t) as (keyof PerfTotals)[])
      t[key] += node.totals[key];
  }
  return t;
}

/** Per-ad rows into campaigns, each holding its ad groups, each holding its ads. */
export function buildTree(rows: PerformanceRow[]): PerfNode[] {
  const campaigns = new Map<string, PerfNode>();
  for (const row of rows) {
    const campaignKey = `${row.store}:${row.campaign_id}`;
    let campaign = campaigns.get(campaignKey);
    if (!campaign) {
      campaign = {
        id: campaignKey,
        level: 'campaign',
        name: row.campaign_name ?? row.campaign_id,
        store: row.store,
        campaignId: row.campaign_id,
        totals: zero(),
        children: [],
      };
      campaigns.set(campaignKey, campaign);
    }
    const adsetKey = `${campaignKey}:${row.adset_id ?? ''}`;
    let adset = campaign.children.find(child => child.id === adsetKey);
    if (!adset) {
      adset = {
        id: adsetKey,
        level: 'adset',
        name: row.adset_name ?? row.adset_id ?? '(no ad group)',
        store: row.store,
        campaignId: row.campaign_id,
        totals: zero(),
        children: [],
      };
      campaign.children.push(adset);
    }
    const ad: PerfNode = {
      id: `${adsetKey}:${row.ad_id ?? ''}`,
      level: 'ad',
      name: row.ad_name ?? row.ad_id ?? '(no ad)',
      store: row.store,
      campaignId: row.campaign_id,
      totals: zero(),
      children: [],
    };
    addRow(ad.totals, row);
    adset.children.push(ad);
    addRow(adset.totals, row);
    addRow(campaign.totals, row);
  }
  return Array.from(campaigns.values());
}

export interface PerfSort {
  key: string;
  dir: 'asc' | 'desc';
}
export const DEFAULT_SORT: PerfSort = { key: 'spend', dir: 'desc' };

/** Sorted at every level. Blank values ("-") always sink to the bottom, whichever way it runs. */
export function sortTree(nodes: PerfNode[], sort: PerfSort): PerfNode[] {
  const column = COLUMN_BY_KEY.get(sort.key);
  const valueOf = (node: PerfNode) =>
    column ? column.value(node.totals) : null;
  const sorted = [...nodes].sort((a, b) => {
    if (!column)
      return a.name.localeCompare(b.name) * (sort.dir === 'asc' ? 1 : -1);
    const x = valueOf(a);
    const y = valueOf(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return sort.dir === 'asc' ? x - y : y - x;
  });
  return sorted.map(node =>
    node.children.length
      ? { ...node, children: sortTree(node.children, sort) }
      : node,
  );
}

// ── Dates ────────────────────────────────────────────────────────────────────

export type DatePreset =
  'today' | 'yesterday' | 'last7' | 'last14' | 'last30' | 'custom';
export const DATE_PRESETS: {
  key: Exclude<DatePreset, 'custom'>;
  label: string;
}[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last14', label: 'Last 14 days' },
  { key: 'last30', label: 'Last 30 days' },
];
export const DEFAULT_PRESET: DatePreset = 'today';
const PT = 'America/Los_Angeles';

/** Today's date in Pacific time, the day the team works in. */
export function todayPT(now: Date): string {
  return dayjs(now).tz(PT).format('YYYY-MM-DD');
}

/**
 * The inclusive from/to dates for a preset. Last 7, 14 and 30 days end yesterday, as Google Ads
 * counts them, so a part-day never sits in a multi-day range; Today is its own button.
 */
export function presetRange(
  preset: Exclude<DatePreset, 'custom'>,
  today: string,
): { from: string; to: string } {
  const day = dayjs(today);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = day.subtract(1, 'day').format('YYYY-MM-DD');
      return { from: y, to: y };
    }
    case 'last7':
    case 'last14':
    case 'last30': {
      const days = preset === 'last7' ? 7 : preset === 'last14' ? 14 : 30;
      return { from: day.subtract(days, 'day').format('YYYY-MM-DD'), to: day.subtract(1, 'day').format('YYYY-MM-DD') };
    }
  }
}

export interface DateRange {
  from: string;
  to: string;
}

/** A range in words: "24 to 30 Sep", "29 Sep to 5 Oct", or "1 Oct" for one day. "and" reads "between 24 and 30 Sep". */
export function rangeWords(range: DateRange, joiner: 'to' | 'and'): string {
  const from = dayjs(range.from);
  const to = dayjs(range.to);
  if (range.from === range.to) return to.format('D MMM');
  const otherYear = from.year() !== to.year();
  const start = from.format(otherYear ? 'D MMM YYYY' : from.month() === to.month() ? 'D' : 'D MMM');
  return `${start} ${joiner} ${to.format(otherYear ? 'D MMM YYYY' : 'D MMM')}`;
}

/**
 * The two periods behind a card's before and after figure: from the earlier change (or the start
 * of the series) to the day before the change, and from the change to the card's latest day.
 * Null when the card found no change day.
 */
export function cardWindows(
  dropStart: { beforeStart: string; mostLikely: string } | null | undefined,
  seriesDates: string[],
): { before: DateRange; after: DateRange } | null {
  const last = seriesDates.reduce<string | null>((latest, date) => (latest == null || date > latest ? date : latest), null);
  if (!dropStart || !last) return null;
  return {
    before: { from: dropStart.beforeStart, to: dayjs(dropStart.mostLikely).subtract(1, 'day').format('YYYY-MM-DD') },
    after: { from: dropStart.mostLikely, to: last },
  };
}
